import React, { useState } from "react";
import { getFileByModule } from "../../services/FilesService/FilesService";
import toast from "react-hot-toast";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../../utils/fileUtils";
import kise from "../../assets/icons/plans_toy.png";
import ortangy from "../../assets/icons/plans_horse.png";
import eresek from "../../assets/icons/plans_plane.png";
import mektepaldy from "../../assets/icons/plans_puzzle.png";

const PerspectivePlans = () => {
  const [isDownloading, setIsDownloading] = useState(null); // Track which card is downloading

  // Use the reusable file preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const [selectedCard, setSelectedCard] = useState(null);

  const cards = [
    {
      title: "Кіші топ",
      description: "Кіші топ перспективалық жоспары",
      group: "kishi_top",
      text: "text-red-500",
      border: "border-red-500",
      hoverBg: "hover:bg-red-500",
      iconSrc: kise,
    },
    {
      title: "Ортаңғы топ",
      description: "Ортаңғы топ перспективалық жоспары",
      group: "ortangy_top",
      text: "text-blue-500",
      border: "border-blue-500",
      hoverBg: "hover:bg-blue-500",
      iconSrc: ortangy,
    },
    {
      title: "Ересек топ",
      description: "Ересек топ перспективалық жоспары",
      group: "eresek_top",
      text: "text-amber-500",
      border: "border-amber-500",
      hoverBg: "hover:bg-amber-500",
      iconSrc: eresek,
    },
    {
      title: "Мектепалды топ",
      description: "Мектепалды топ перспективалық жоспары",
      group: "mektep_aldy_top",
      text: "text-green-500",
      border: "border-green-500",
      hoverBg: "hover:bg-green-500",
      iconSrc: mektepaldy,
    },
  ];

  const fetchFileMeta = async (group) => {
    try {
      const response = await getFileByModule({
        module_code: "perspektiva",
        group,
      });

      if (!response?.file) {
        toast.error("Файл табылмады.");
        return null;
      }
      return response.file;
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Файл табылмады немесе қате болды.");
      return null;
    }
  };

  const handlePreview = async (card) => {
    const file = await fetchFileMeta(card.group);
    if (!file) return;

    setSelectedCard(card);
    await openPreview(file.file_url, file.file_name);
  };

  const handleDownload = async (group, cardIndex) => {
    setIsDownloading(cardIndex);
    try {
      const file = await fetchFileMeta(group);
      if (!file) return;

      downloadFile(file.file_url, file.file_name);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Файл жүктеу кезінде қате болды.");
    } finally {
      setIsDownloading(null);
    }
  };

  return (
    <div className="flex flex-col items-center justify-start px-4 pt-16 pb-8">
      <h1 className="text-3xl text-[#674DA2] mb-12 text-center">
        Перспективалық жоспарлар
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl w-full">
        {cards.map((card, index) => (
          <div
            key={index}
            className={`group relative h-48 rounded-2xl p-6 text-center transition-all transform hover:scale-105 hover:shadow-lg cursor-pointer bg-white border ${card.border} ${card.hoverBg}`}
          >
            {/* Default state - shows icon and title */}
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-300 group-hover:opacity-0 ${card.text}`}
            >
              <img
                src={card.iconSrc}
                alt={card.title}
                className="w-10 h-10 mb-2"
              />
              <h2 className="text-lg font-semibold">{card.title}</h2>
            </div>

            {/* Hover state - shows description and action buttons */}
            <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 px-4">
              <p className="text-sm font-medium text-white mb-4 text-center">
                {card.description}
              </p>

              {/* Action buttons */}
              <div className="flex gap-2 w-full">
                {/* Preview button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(card);
                  }}
                  disabled={isPreviewLoading}
                  className={`flex-1 bg-white font-semibold px-3 py-2 rounded hover:bg-gray-100 transition text-sm ${card.text} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1`}
                >
                  {isPreviewLoading ? (
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span className="hidden sm:inline">Көру</span>
                    </>
                  )}
                </button>

                {/* Download button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(card.group, index);
                  }}
                  disabled={isDownloading === index}
                  className={`flex-1 bg-white font-semibold px-3 py-2 rounded hover:bg-gray-100 transition text-sm ${card.text} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1`}
                >
                  {isDownloading === index ? (
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="hidden sm:inline">Жүктеу</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={
          selectedCard
            ? `${selectedCard.title} - Перспективалық жоспар`
            : "Алдын ала қарау"
        }
      />
    </div>
  );
};

export default PerspectivePlans;
